import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Eye,
  Loader2,
  Megaphone,
  PencilLine,
  Plus,
  Printer,
  Save,
  Search,
  Trash2,
  Utensils,
  Wine,
  X,
  XCircle,
} from 'lucide-react';
import clsx from 'clsx';
import { menuApi, preshiftConfigApi } from '../../services/api';
import type { Announcement, EightySixItem, Special } from '../../hooks/useShiftSetupData';

interface MenuSetupPanelProps {
  specials: Special[];
  eightySixItems: EightySixItem[];
  announcements: Announcement[];
  shiftDate: string;
  shiftStartTime: string;
  shiftEndTime: string;
}

type ExpandableSection = 'specials' | 'eightysix' | 'announcements' | 'briefing' | null;
type ViewMode = 'setup' | 'staff-briefing';
type AnnouncementPriority = 'urgent' | 'high' | 'normal' | 'low';

const PRIORITY_COLORS: Record<AnnouncementPriority, string> = {
  urgent: 'bg-red-100 text-red-700 border-red-200',
  high: 'bg-amber-100 text-amber-700 border-amber-200',
  normal: 'bg-blue-100 text-blue-700 border-blue-200',
  low: 'bg-slate-100 text-slate-600 border-slate-200',
};

const ROLE_OPTIONS = ['server', 'bartender', 'host', 'cook', 'manager', 'busser', 'dishwasher'] as const;

function formatCurrency(cents?: number | null) {
  if (typeof cents !== 'number') return null;
  return `$${(cents / 100).toFixed(2)}`;
}

function toDateTimeLocal(value?: string) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (num: number) => String(num).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function splitTextList(value: string) {
  return value
    .split(/\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);
}

export default function MenuSetupPanel({
  specials,
  eightySixItems,
  announcements,
  shiftDate,
  shiftStartTime,
  shiftEndTime,
}: MenuSetupPanelProps) {
  const queryClient = useQueryClient();

  // Normalize date to YYYY-MM-DD format for API calls
  const normalizedDate = shiftDate
    ? (typeof shiftDate === 'string' ? shiftDate.split('T')[0] : new Date(shiftDate).toISOString().split('T')[0])
    : '';
  const [viewMode, setViewMode] = useState<ViewMode>('setup');
  const [expandedSection, setExpandedSection] = useState<ExpandableSection>('specials');
  const [showAddSpecial, setShowAddSpecial] = useState(false);
  const [showAddAnnouncement, setShowAddAnnouncement] = useState(false);
  const [specialSearch, setSpecialSearch] = useState('');
  const [specialPrice, setSpecialPrice] = useState('');
  const [specialDescription, setSpecialDescription] = useState('');
  const [selectedSpecialItem, setSelectedSpecialItem] = useState<{
    id: string;
    name: string;
    priceCents: number;
  } | null>(null);
  const [eightySixSearch, setEightySixSearch] = useState('');
  const [announcementForm, setAnnouncementForm] = useState<{
    title: string;
    message: string;
    priority: AnnouncementPriority;
    targetRoles: string[];
    startsAt: string;
    expiresAt: string;
  }>({
    title: '',
    message: '',
    priority: 'normal',
    targetRoles: [],
    startsAt: '',
    expiresAt: '',
  });
  const [briefingSearch, setBriefingSearch] = useState('');
  const [selectedBriefingItemId, setSelectedBriefingItemId] = useState<string | null>(null);
  const [briefingForm, setBriefingForm] = useState({
    staffDescription: '',
    allergenInfo: '',
    ingredientsList: '',
    pairingSuggestions: '',
    prepNotes: '',
  });
  // New item creation state
  const [showCreateMenuItem, setShowCreateMenuItem] = useState(false);
  const [newMenuItemForm, setNewMenuItemForm] = useState({
    name: '',
    description: '',
    priceCents: '',
    categoryId: '',
  });

  useEffect(() => {
    setAnnouncementForm((current) => ({
      ...current,
      startsAt: current.startsAt || toDateTimeLocal(shiftStartTime),
      expiresAt: current.expiresAt || toDateTimeLocal(shiftEndTime),
    }));
  }, [shiftStartTime, shiftEndTime]);

  const activeAnnouncements = useMemo(
    () => announcements.filter((announcement) => announcement.isActive),
    [announcements]
  );

  const filteredAvailabilityItems = useMemo(() => {
    const search = eightySixSearch.trim().toLowerCase();
    if (!search) return eightySixItems;
    return eightySixItems.filter((item) => item.name.toLowerCase().includes(search));
  }, [eightySixItems, eightySixSearch]);

  const specialSearchQuery = useQuery({
    queryKey: ['shift-setup-special-search', specialSearch],
    queryFn: async () => {
      const response = await preshiftConfigApi.searchMenuItems(specialSearch);
      return response.data?.data || [];
    },
    enabled: showAddSpecial && specialSearch.trim().length >= 2,
  });

  const briefingSearchQuery = useQuery({
    queryKey: ['shift-setup-briefing-search', briefingSearch],
    queryFn: async () => {
      const response = await preshiftConfigApi.searchMenuItems(briefingSearch);
      return response.data?.data || [];
    },
    enabled: expandedSection === 'briefing' && briefingSearch.trim().length >= 2,
  });

  // Get menu categories for creating new items
  const categoriesQuery = useQuery({
    queryKey: ['menu-categories'],
    queryFn: async () => {
      const response = await menuApi.getCategories();
      return response.data?.data || response.data || [];
    },
    enabled: showCreateMenuItem,
  });

  const selectedBriefingItemQuery = useQuery({
    queryKey: ['shift-setup-menu-item', selectedBriefingItemId],
    queryFn: async () => {
      if (!selectedBriefingItemId) return null;
      const response = await menuApi.getItem(selectedBriefingItemId);
      return response.data?.data || response.data;
    },
    enabled: !!selectedBriefingItemId,
  });

  useEffect(() => {
    const item = selectedBriefingItemQuery.data;
    if (!item) return;

    setBriefingForm({
      staffDescription: item.staffDescription || '',
      allergenInfo: item.allergenInfo || '',
      ingredientsList: item.ingredientsList || '',
      pairingSuggestions: Array.isArray(item.pairingSuggestions)
        ? item.pairingSuggestions.join(', ')
        : '',
      prepNotes: item.prepNotes || '',
    });
  }, [selectedBriefingItemQuery.data]);

  const invalidateBriefingQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['specials', normalizedDate] });
    queryClient.invalidateQueries({ queryKey: ['announcements'] });
    queryClient.invalidateQueries({ queryKey: ['eighty-six'] });
    queryClient.invalidateQueries({ queryKey: ['menu'] });
    queryClient.invalidateQueries({ queryKey: ['shift-instance'] });
  };

  const addSpecialMutation = useMutation({
    mutationFn: async () => {
      if (!selectedSpecialItem) {
        throw new Error('Select a menu item first');
      }

      return preshiftConfigApi.addSpecial({
        menuItemId: selectedSpecialItem.id,
        date: normalizedDate,
        specialPriceCents: specialPrice ? Math.round(parseFloat(specialPrice) * 100) : undefined,
        description: specialDescription || undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specials', normalizedDate] });
      setShowAddSpecial(false);
      setSpecialSearch('');
      setSelectedSpecialItem(null);
      setSpecialPrice('');
      setSpecialDescription('');
    },
  });

  // Create new menu item and add as special
  const createMenuItemMutation = useMutation({
    mutationFn: async () => {
      if (!newMenuItemForm.name.trim() || !newMenuItemForm.priceCents) {
        throw new Error('Name and price are required');
      }

      // Create the menu item first
      const itemData: any = {
        name: newMenuItemForm.name.trim(),
        priceCents: Math.round(parseFloat(newMenuItemForm.priceCents) * 100),
      };
      if (newMenuItemForm.description.trim()) {
        itemData.description = newMenuItemForm.description.trim();
      }
      if (newMenuItemForm.categoryId) {
        itemData.categoryId = newMenuItemForm.categoryId;
      }
      const createResponse = await menuApi.createItem(itemData);

      const newItem = createResponse.data?.data || createResponse.data;
      if (!newItem?.id) {
        throw new Error('Failed to create menu item');
      }

      // Then add it as a special
      await preshiftConfigApi.addSpecial({
        menuItemId: newItem.id,
        date: normalizedDate,
        specialPriceCents: specialPrice ? Math.round(parseFloat(specialPrice) * 100) : undefined,
        description: specialDescription || undefined,
      });

      return newItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specials', normalizedDate] });
      queryClient.invalidateQueries({ queryKey: ['menu'] });
      setShowAddSpecial(false);
      setShowCreateMenuItem(false);
      setSpecialSearch('');
      setSpecialPrice('');
      setSpecialDescription('');
      setNewMenuItemForm({ name: '', description: '', priceCents: '', categoryId: '' });
    },
  });

  const removeSpecialMutation = useMutation({
    mutationFn: (specialId: string) => preshiftConfigApi.removeSpecial(specialId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specials', normalizedDate] });
    },
  });

  const toggleAvailabilityMutation = useMutation({
    mutationFn: ({ menuItemId, isAvailable }: { menuItemId: string; isAvailable: boolean }) =>
      preshiftConfigApi.toggleEightySix(menuItemId, { isAvailable }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eighty-six'] });
      queryClient.invalidateQueries({ queryKey: ['menu'] });
    },
  });

  const addAnnouncementMutation = useMutation({
    mutationFn: () =>
      preshiftConfigApi.createAnnouncement({
        title: announcementForm.title.trim(),
        message: announcementForm.message.trim(),
        priority: announcementForm.priority,
        targetRoles: announcementForm.targetRoles,
        startsAt: announcementForm.startsAt ? new Date(announcementForm.startsAt).toISOString() : undefined,
        expiresAt: announcementForm.expiresAt ? new Date(announcementForm.expiresAt).toISOString() : undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
      setShowAddAnnouncement(false);
      setAnnouncementForm({
        title: '',
        message: '',
        priority: 'normal',
        targetRoles: [],
        startsAt: toDateTimeLocal(shiftStartTime),
        expiresAt: toDateTimeLocal(shiftEndTime),
      });
    },
  });

  const removeAnnouncementMutation = useMutation({
    mutationFn: (announcementId: string) => preshiftConfigApi.deleteAnnouncement(announcementId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
    },
  });

  const saveBriefingMutation = useMutation({
    mutationFn: async () => {
      if (!selectedBriefingItemId) {
        throw new Error('Select a menu item first');
      }

      return preshiftConfigApi.updateMenuItemDetails(selectedBriefingItemId, {
        staffDescription: briefingForm.staffDescription || undefined,
        allergenInfo: briefingForm.allergenInfo || undefined,
        ingredientsList: briefingForm.ingredientsList || undefined,
        pairingSuggestions: splitTextList(briefingForm.pairingSuggestions),
        prepNotes: briefingForm.prepNotes || undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shift-setup-menu-item', selectedBriefingItemId] });
      invalidateBriefingQueries();
    },
  });

  const toggleSection = (section: ExpandableSection) => {
    setExpandedSection((current) => (current === section ? null : section));
  };

  const renderToggle = (section: ExpandableSection) =>
    expandedSection === section ? (
      <ChevronUp className="w-4 h-4 text-slate-400" />
    ) : (
      <ChevronDown className="w-4 h-4 text-slate-400" />
    );

  // Items with briefing info for Staff Briefing view
  const briefingItems = useMemo(() => {
    const items: Array<{
      id: string;
      name: string;
      type: 'special' | '86d';
      priceCents?: number;
      specialPriceCents?: number;
      staffDescription?: string;
      allergenInfo?: string;
      ingredientsList?: string;
      pairingSuggestions?: string[];
      prepNotes?: string;
      description?: string;
    }> = [];

    // Add specials
    for (const special of specials) {
      items.push({
        id: special.id,
        name: special.menuItem?.name || 'Unknown',
        type: 'special',
        priceCents: special.regularPriceCents,
        specialPriceCents: special.specialPriceCents,
        staffDescription: special.staffDescription || special.menuItem?.staffDescription,
        allergenInfo: special.menuItem?.allergenInfo,
        ingredientsList: special.menuItem?.ingredientsList,
        pairingSuggestions: special.menuItem?.pairingSuggestions,
        prepNotes: special.menuItem?.prepNotes,
        description: special.description,
      });
    }

    // Add 86'd items
    for (const item of eightySixItems.filter(i => !i.isAvailable)) {
      items.push({
        id: item.id,
        name: item.name,
        type: '86d',
        staffDescription: item.staffDescription,
        allergenInfo: item.allergenInfo,
        prepNotes: item.prepNotes,
      });
    }

    return items;
  }, [specials, eightySixItems]);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden print:border-0 print:shadow-none">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50/50 print:bg-white">
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900 uppercase tracking-wide">
            <Utensils className="w-4 h-4 print:hidden" />
            Shift Briefing
          </h3>
          <div className="flex items-center gap-2 print:hidden">
            <button
              onClick={() => setViewMode(viewMode === 'setup' ? 'staff-briefing' : 'setup')}
              className={clsx(
                'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors',
                viewMode === 'staff-briefing'
                  ? 'bg-indigo-100 text-indigo-700 border-indigo-200'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              )}
            >
              <Eye className="w-3.5 h-3.5" />
              {viewMode === 'staff-briefing' ? 'Exit Briefing View' : 'Staff Briefing'}
            </button>
            {viewMode === 'staff-briefing' && (
              <button
                onClick={handlePrint}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
              >
                <Printer className="w-3.5 h-3.5" />
                Print
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Staff Briefing View - Full expanded view of all specials and 86'd items */}
      {viewMode === 'staff-briefing' ? (
        <div className="p-4 space-y-6 print:p-0">
          {/* Print header - only visible when printing */}
          <div className="hidden print:block print:mb-6">
            <h1 className="text-2xl font-bold text-slate-900">Pre-Shift Briefing</h1>
            <p className="text-sm text-slate-500 mt-1">
              {new Date(shiftDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>

          {/* Specials Section */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <DollarSign className="w-5 h-5 text-emerald-500 print:text-emerald-700" />
              <h4 className="text-base font-semibold text-slate-900">Today's Specials</h4>
              <span className="px-2 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-full">
                {specials.length}
              </span>
            </div>
            {specials.length === 0 ? (
              <p className="text-sm text-slate-500 italic ml-7">No specials for this shift.</p>
            ) : (
              <div className="space-y-4 ml-7">
                {specials.map((special) => (
                  <div key={special.id} className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl print:bg-white print:border-slate-300">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h5 className="font-semibold text-slate-900 text-base">{special.menuItem?.name}</h5>
                        <div className="flex gap-3 mt-1 text-sm">
                          {special.regularPriceCents !== undefined && (
                            <span className="text-slate-500">Regular: {formatCurrency(special.regularPriceCents)}</span>
                          )}
                          {special.specialPriceCents !== undefined && (
                            <span className="text-emerald-700 font-medium">Special: {formatCurrency(special.specialPriceCents)}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Staff talking points */}
                    {(special.staffDescription || special.menuItem?.staffDescription || special.description) && (
                      <div className="mt-3 p-3 bg-white rounded-lg border border-emerald-100 print:bg-slate-50">
                        <p className="text-xs font-medium text-emerald-700 uppercase mb-1">Talking Points</p>
                        <p className="text-sm text-slate-700">
                          {special.staffDescription || special.menuItem?.staffDescription || special.description}
                        </p>
                      </div>
                    )}

                    {/* Allergen info - highlighted */}
                    {special.menuItem?.allergenInfo && (
                      <div className="mt-2 p-3 bg-amber-50 rounded-lg border border-amber-200 print:bg-amber-50">
                        <p className="text-xs font-medium text-amber-700 uppercase mb-1 flex items-center gap-1.5">
                          <AlertTriangle className="w-3.5 h-3.5" />
                          Allergen Information
                        </p>
                        <p className="text-sm text-amber-800 font-medium">{special.menuItem.allergenInfo}</p>
                      </div>
                    )}

                    {/* Additional info grid */}
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 print:grid-cols-2">
                      {special.menuItem?.ingredientsList && (
                        <div className="p-2 bg-white rounded border border-slate-100 print:bg-slate-50">
                          <p className="text-xs font-medium text-slate-500 uppercase mb-0.5">Key Ingredients</p>
                          <p className="text-sm text-slate-700">{special.menuItem.ingredientsList}</p>
                        </div>
                      )}
                      {special.menuItem?.pairingSuggestions && special.menuItem.pairingSuggestions.length > 0 && (
                        <div className="p-2 bg-white rounded border border-slate-100 print:bg-slate-50">
                          <p className="text-xs font-medium text-slate-500 uppercase mb-0.5 flex items-center gap-1">
                            <Wine className="w-3 h-3" />
                            Pairing Suggestions
                          </p>
                          <p className="text-sm text-slate-700">
                            {Array.isArray(special.menuItem.pairingSuggestions)
                              ? special.menuItem.pairingSuggestions.join(', ')
                              : special.menuItem.pairingSuggestions}
                          </p>
                        </div>
                      )}
                      {special.menuItem?.prepNotes && (
                        <div className="p-2 bg-white rounded border border-slate-100 print:bg-slate-50 sm:col-span-2 print:col-span-2">
                          <p className="text-xs font-medium text-slate-500 uppercase mb-0.5">Prep Notes</p>
                          <p className="text-sm text-slate-700">{special.menuItem.prepNotes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 86'd Items Section */}
          <div className="print:break-before-auto">
            <div className="flex items-center gap-2 mb-3">
              <XCircle className="w-5 h-5 text-red-500 print:text-red-700" />
              <h4 className="text-base font-semibold text-slate-900">86'd Items</h4>
              <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                {eightySixItems.filter(i => !i.isAvailable).length}
              </span>
            </div>
            {eightySixItems.filter(i => !i.isAvailable).length === 0 ? (
              <p className="text-sm text-slate-500 italic ml-7">All menu items are available.</p>
            ) : (
              <div className="space-y-2 ml-7">
                {eightySixItems.filter(i => !i.isAvailable).map((item) => (
                  <div key={item.id} className="p-3 bg-red-50 border border-red-200 rounded-lg print:bg-white print:border-red-300">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-red-700 line-through">{item.name}</span>
                      <span className="px-2 py-0.5 text-[11px] font-medium rounded-full bg-red-100 text-red-700">86'd</span>
                    </div>
                    {item.prepNotes && (
                      <p className="text-sm text-red-600 mt-1">{item.prepNotes}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Announcements Section */}
          {activeAnnouncements.length > 0 && (
            <div className="print:break-before-auto">
              <div className="flex items-center gap-2 mb-3">
                <Megaphone className="w-5 h-5 text-indigo-500 print:text-indigo-700" />
                <h4 className="text-base font-semibold text-slate-900">Shift Announcements</h4>
                <span className="px-2 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-700 rounded-full">
                  {activeAnnouncements.length}
                </span>
              </div>
              <div className="space-y-2 ml-7">
                {activeAnnouncements.map((announcement) => (
                  <div
                    key={announcement.id}
                    className={clsx(
                      'p-3 rounded-lg border print:bg-white',
                      PRIORITY_COLORS[(announcement.priority || 'normal') as AnnouncementPriority] || PRIORITY_COLORS.normal
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{announcement.title}</p>
                      <span className="text-[11px] uppercase opacity-70">{announcement.priority}</span>
                    </div>
                    <p className="text-sm mt-1 opacity-90">{announcement.message}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Setup View - Original expandable sections */

      <div className="divide-y divide-slate-100">
        <div>
          <button
            onClick={() => toggleSection('specials')}
            className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-slate-50 active:bg-slate-100 transition-colors touch-manipulation"
          >
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-emerald-500" />
              <span className="text-sm font-medium text-slate-900">Daily Specials</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={clsx('px-2 py-0.5 text-xs font-medium rounded-full', specials.length > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500')}>
                {specials.length}
              </span>
              {renderToggle('specials')}
            </div>
          </button>

          {expandedSection === 'specials' && (
            <div className="px-4 pb-4 space-y-4">
              <div className="flex items-center justify-between pt-1">
                <p className="text-sm text-slate-500">Attach specials directly to this shift date.</p>
                <button
                  onClick={() => setShowAddSpecial((current) => !current)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors"
                >
                  {showAddSpecial ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                  {showAddSpecial ? 'Close' : 'Add Special'}
                </button>
              </div>

              {showAddSpecial && (
                <div className="p-4 rounded-xl border border-emerald-200 bg-emerald-50/50 space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 uppercase mb-1">Search menu</label>
                    <div className="relative">
                      <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        value={specialSearch}
                        onChange={(event) => setSpecialSearch(event.target.value)}
                        className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm"
                        placeholder="Search by menu item name"
                      />
                    </div>
                    {specialSearch.trim().length >= 2 && !showCreateMenuItem && (
                      <div className="mt-2 max-h-48 overflow-y-auto bg-white border border-slate-200 rounded-lg">
                        {specialSearchQuery.isLoading ? (
                          <div className="p-3 flex justify-center">
                            <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                          </div>
                        ) : !(specialSearchQuery.data?.length) ? (
                          <div className="p-3 text-center">
                            <p className="text-sm text-slate-500 mb-2">No menu items found for "{specialSearch}"</p>
                            <button
                              onClick={() => {
                                setShowCreateMenuItem(true);
                                setNewMenuItemForm((prev) => ({ ...prev, name: specialSearch }));
                              }}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors"
                            >
                              <Plus className="w-3.5 h-3.5" />
                              Create "{specialSearch}" as new item
                            </button>
                          </div>
                        ) : (
                          <>
                            {specialSearchQuery.data.map((item: any) => (
                              <button
                                key={item.id}
                                onClick={() => {
                                  setSelectedSpecialItem(item);
                                  setSpecialSearch('');
                                }}
                                className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-50 active:bg-slate-100 text-sm text-left touch-manipulation"
                              >
                                <span className="text-slate-900">{item.name}</span>
                                <span className="text-slate-500">{formatCurrency(item.priceCents)}</span>
                              </button>
                            ))}
                            <button
                              onClick={() => {
                                setShowCreateMenuItem(true);
                                setNewMenuItemForm((prev) => ({ ...prev, name: specialSearch }));
                              }}
                              className="w-full flex items-center gap-1.5 px-3 py-2 text-sm text-emerald-600 hover:bg-emerald-50 border-t border-slate-100 touch-manipulation"
                            >
                              <Plus className="w-3.5 h-3.5" />
                              Create new menu item
                            </button>
                          </>
                        )}
                      </div>
                    )}

                    {/* Create new menu item form */}
                    {showCreateMenuItem && (
                      <div className="mt-3 p-3 bg-white border border-emerald-200 rounded-lg space-y-3">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-emerald-700">Create New Menu Item</p>
                          <button
                            onClick={() => setShowCreateMenuItem(false)}
                            className="p-1 text-slate-400 hover:text-slate-600 rounded touch-manipulation"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 uppercase mb-1">Item Name *</label>
                          <input
                            value={newMenuItemForm.name}
                            onChange={(e) => setNewMenuItemForm((prev) => ({ ...prev, name: e.target.value }))}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                            placeholder="e.g., Pan-Seared Salmon"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-slate-600 uppercase mb-1">Regular Price *</label>
                            <input
                              type="number"
                              step="0.01"
                              value={newMenuItemForm.priceCents}
                              onChange={(e) => setNewMenuItemForm((prev) => ({ ...prev, priceCents: e.target.value }))}
                              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                              placeholder="24.99"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-600 uppercase mb-1">Category</label>
                            <select
                              value={newMenuItemForm.categoryId}
                              onChange={(e) => setNewMenuItemForm((prev) => ({ ...prev, categoryId: e.target.value }))}
                              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                            >
                              <option value="">Select category</option>
                              {(categoriesQuery.data || []).map((cat: any) => (
                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 uppercase mb-1">Description</label>
                          <textarea
                            value={newMenuItemForm.description}
                            onChange={(e) => setNewMenuItemForm((prev) => ({ ...prev, description: e.target.value }))}
                            rows={2}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none"
                            placeholder="Brief description for the menu"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {selectedSpecialItem && (
                    <div className="flex items-center justify-between p-3 bg-white border border-emerald-200 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-slate-900">{selectedSpecialItem.name}</p>
                        <p className="text-xs text-slate-500">Regular price: {formatCurrency(selectedSpecialItem.priceCents)}</p>
                      </div>
                      <button
                        onClick={() => setSelectedSpecialItem(null)}
                        className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 uppercase mb-1">Special price</label>
                      <input
                        type="number"
                        step="0.01"
                        value={specialPrice}
                        onChange={(event) => setSpecialPrice(event.target.value)}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                        placeholder="Optional"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 uppercase mb-1">Shift date</label>
                      <input
                        value={shiftDate}
                        readOnly
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 text-slate-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-600 uppercase mb-1">Special note</label>
                    <textarea
                      value={specialDescription}
                      onChange={(event) => setSpecialDescription(event.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none"
                      placeholder="Chef callout, talking points, or shift-specific note"
                    />
                  </div>

                  <div className="flex justify-end gap-2">
                    {showCreateMenuItem ? (
                      <button
                        onClick={() => createMenuItemMutation.mutate()}
                        disabled={!newMenuItemForm.name.trim() || !newMenuItemForm.priceCents || createMenuItemMutation.isPending}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 active:bg-emerald-800 disabled:opacity-50 touch-manipulation"
                      >
                        {createMenuItemMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                        Create & Add as Special
                      </button>
                    ) : (
                      <button
                        onClick={() => addSpecialMutation.mutate()}
                        disabled={!selectedSpecialItem || addSpecialMutation.isPending}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 active:bg-emerald-800 disabled:opacity-50 touch-manipulation"
                      >
                        {addSpecialMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                        Add to Shift
                      </button>
                    )}
                  </div>
                </div>
              )}

              {!specials.length ? (
                <p className="text-sm text-slate-500 italic">No specials added for this shift yet.</p>
              ) : (
                <div className="space-y-2">
                  {specials.map((special) => (
                    <div key={special.id} className="flex items-start justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900">
                          {special.menuItem?.name || 'Unknown menu item'}
                        </p>
                        <div className="flex flex-wrap gap-3 mt-1 text-xs text-slate-500">
                          {special.regularPriceCents !== undefined && <span>Regular: {formatCurrency(special.regularPriceCents)}</span>}
                          {special.specialPriceCents !== undefined && <span className="text-emerald-700 font-medium">Special: {formatCurrency(special.specialPriceCents)}</span>}
                        </div>
                        {(special.description || special.staffDescription) && (
                          <p className="text-xs text-slate-600 mt-2">
                            {special.description || special.staffDescription}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => removeSpecialMutation.mutate(special.id)}
                        className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div>
          <button
            onClick={() => toggleSection('eightysix')}
            className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-slate-50 active:bg-slate-100 transition-colors touch-manipulation"
          >
            <div className="flex items-center gap-2">
              <XCircle className="w-4 h-4 text-red-500" />
              <span className="text-sm font-medium text-slate-900">Availability & 86s</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={clsx('px-2 py-0.5 text-xs font-medium rounded-full', eightySixItems.some((item) => !item.isAvailable) ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-500')}>
                {eightySixItems.filter((item) => !item.isAvailable).length}
              </span>
              {renderToggle('eightysix')}
            </div>
          </button>

          {expandedSection === 'eightysix' && (
            <div className="px-4 pb-4 space-y-3">
              <p className="pt-1 text-sm text-slate-500">
                Toggle live menu availability so POS and KDS stay in sync.
              </p>
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  value={eightySixSearch}
                  onChange={(event) => setEightySixSearch(event.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm"
                  placeholder="Search menu items"
                />
              </div>

              <div className="max-h-72 overflow-y-auto space-y-2">
                {filteredAvailabilityItems.map((item) => (
                  <label
                    key={item.id}
                    className={clsx(
                      'flex items-start justify-between gap-3 p-3 rounded-lg border transition-colors cursor-pointer',
                      item.isAvailable ? 'bg-slate-50 border-slate-200 hover:bg-slate-100' : 'bg-red-50 border-red-200'
                    )}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={clsx('text-sm font-medium', item.isAvailable ? 'text-slate-900' : 'text-red-700 line-through')}>
                          {item.name}
                        </span>
                        {!item.isAvailable && (
                          <span className="px-2 py-0.5 text-[11px] font-medium rounded-full bg-red-100 text-red-700">
                            86'd
                          </span>
                        )}
                      </div>
                      {item.availableQuantity !== undefined && item.availableQuantity !== null && item.isAvailable && (
                        <p className="text-xs text-amber-700 mt-1">{item.availableQuantity} left</p>
                      )}
                      {item.prepNotes && (
                        <p className="text-xs text-slate-500 mt-1">{item.prepNotes}</p>
                      )}
                    </div>
                    <input
                      type="checkbox"
                      checked={item.isAvailable}
                      onChange={(event) =>
                        toggleAvailabilityMutation.mutate({
                          menuItemId: item.id,
                          isAvailable: event.target.checked,
                        })
                      }
                      className="mt-1 w-4 h-4 accent-indigo-600"
                    />
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        <div>
          <button
            onClick={() => toggleSection('announcements')}
            className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-slate-50 active:bg-slate-100 transition-colors touch-manipulation"
          >
            <div className="flex items-center gap-2">
              <Megaphone className="w-4 h-4 text-indigo-500" />
              <span className="text-sm font-medium text-slate-900">Shift Announcements</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={clsx('px-2 py-0.5 text-xs font-medium rounded-full', activeAnnouncements.length > 0 ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-500')}>
                {activeAnnouncements.length}
              </span>
              {renderToggle('announcements')}
            </div>
          </button>

          {expandedSection === 'announcements' && (
            <div className="px-4 pb-4 space-y-4">
              <div className="flex items-center justify-between pt-1">
                <p className="text-sm text-slate-500">Brief the team with shift-specific callouts.</p>
                <button
                  onClick={() => setShowAddAnnouncement((current) => !current)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
                >
                  {showAddAnnouncement ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                  {showAddAnnouncement ? 'Close' : 'Add Announcement'}
                </button>
              </div>

              {showAddAnnouncement && (
                <div className="p-4 rounded-xl border border-indigo-200 bg-indigo-50/50 space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 uppercase mb-1">Title</label>
                    <input
                      value={announcementForm.title}
                      onChange={(event) => setAnnouncementForm((current) => ({ ...current, title: event.target.value }))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                      placeholder="What should staff know?"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-600 uppercase mb-1">Message</label>
                    <textarea
                      value={announcementForm.message}
                      onChange={(event) => setAnnouncementForm((current) => ({ ...current, message: event.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none"
                      placeholder="VIP visit, patio closed, special pacing instructions..."
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-600 uppercase mb-1">Priority</label>
                    <div className="flex flex-wrap gap-2">
                      {(Object.keys(PRIORITY_COLORS) as AnnouncementPriority[]).map((priority) => (
                        <button
                          key={priority}
                          onClick={() => setAnnouncementForm((current) => ({ ...current, priority }))}
                          className={clsx(
                            'px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors',
                            announcementForm.priority === priority
                              ? PRIORITY_COLORS[priority]
                              : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                          )}
                        >
                          {priority}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-600 uppercase mb-1">Target roles</label>
                    <div className="flex flex-wrap gap-2">
                      {ROLE_OPTIONS.map((role) => {
                        const selected = announcementForm.targetRoles.includes(role);
                        return (
                          <button
                            key={role}
                            onClick={() =>
                              setAnnouncementForm((current) => ({
                                ...current,
                                targetRoles: selected
                                  ? current.targetRoles.filter((item) => item !== role)
                                  : [...current.targetRoles, role],
                              }))
                            }
                            className={clsx(
                              'px-3 py-1.5 text-xs rounded-lg border transition-colors',
                              selected
                                ? 'bg-indigo-100 border-indigo-300 text-indigo-700'
                                : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                            )}
                          >
                            {role}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1">Leave empty to show to everyone on shift.</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 uppercase mb-1">Starts</label>
                      <input
                        type="datetime-local"
                        value={announcementForm.startsAt}
                        onChange={(event) => setAnnouncementForm((current) => ({ ...current, startsAt: event.target.value }))}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 uppercase mb-1">Expires</label>
                      <input
                        type="datetime-local"
                        value={announcementForm.expiresAt}
                        onChange={(event) => setAnnouncementForm((current) => ({ ...current, expiresAt: event.target.value }))}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={() => addAnnouncementMutation.mutate()}
                      disabled={!announcementForm.title.trim() || !announcementForm.message.trim() || addAnnouncementMutation.isPending}
                      className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {addAnnouncementMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                      Save Announcement
                    </button>
                  </div>
                </div>
              )}

              {!activeAnnouncements.length ? (
                <p className="text-sm text-slate-500 italic">No active shift announcements.</p>
              ) : (
                <div className="space-y-2">
                  {activeAnnouncements.map((announcement) => (
                    <div
                      key={announcement.id}
                      className={clsx('p-3 rounded-lg border', PRIORITY_COLORS[(announcement.priority || 'normal') as AnnouncementPriority] || PRIORITY_COLORS.normal)}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium">{announcement.title}</p>
                            <span className="text-[11px] uppercase opacity-70">{announcement.priority}</span>
                          </div>
                          <p className="text-xs mt-1 opacity-90">{announcement.message}</p>
                        </div>
                        <button
                          onClick={() => removeAnnouncementMutation.mutate(announcement.id)}
                          className="p-1.5 opacity-60 hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div>
          <button
            onClick={() => toggleSection('briefing')}
            className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-slate-50 active:bg-slate-100 transition-colors touch-manipulation"
          >
            <div className="flex items-center gap-2">
              <PencilLine className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-900">Menu Briefing Notes</span>
            </div>
            {renderToggle('briefing')}
          </button>

          {expandedSection === 'briefing' && (
            <div className="px-4 pb-4 space-y-4">
              <p className="pt-1 text-sm text-slate-500">
                Update staff-facing notes so POS pre-shift and service talking points stay accurate.
              </p>

              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  value={briefingSearch}
                  onChange={(event) => setBriefingSearch(event.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm"
                  placeholder="Search a menu item to edit its briefing"
                />
              </div>

              {briefingSearch.trim().length >= 2 && (
                <div className="max-h-48 overflow-y-auto bg-white border border-slate-200 rounded-lg">
                  {briefingSearchQuery.isLoading ? (
                    <div className="p-3 flex justify-center">
                      <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
                    </div>
                  ) : !(briefingSearchQuery.data?.length) ? (
                    <p className="p-3 text-sm text-slate-500 text-center">No menu items found</p>
                  ) : (
                    briefingSearchQuery.data.map((item: any) => (
                      <button
                        key={item.id}
                        onClick={() => setSelectedBriefingItemId(item.id)}
                        className={clsx(
                          'w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-slate-50 text-left',
                          selectedBriefingItemId === item.id && 'bg-slate-50'
                        )}
                      >
                        <span className="text-slate-900">{item.name}</span>
                        <span className="text-slate-500">{formatCurrency(item.priceCents)}</span>
                      </button>
                    ))
                  )}
                </div>
              )}

              {selectedBriefingItemId && (
                <div className="p-4 rounded-xl border border-slate-200 bg-slate-50/50 space-y-3">
                  {selectedBriefingItemQuery.isLoading ? (
                    <div className="flex justify-center py-6">
                      <Loader2 className="w-5 h-5 animate-spin text-slate-500" />
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">
                            {selectedBriefingItemQuery.data?.name || 'Selected menu item'}
                          </p>
                          {selectedBriefingItemQuery.data?.description && (
                            <p className="text-xs text-slate-500 mt-1">{selectedBriefingItemQuery.data.description}</p>
                          )}
                        </div>
                        <button
                          onClick={() => setSelectedBriefingItemId(null)}
                          className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-slate-600 uppercase mb-1">How staff should describe it</label>
                        <textarea
                          value={briefingForm.staffDescription}
                          onChange={(event) => setBriefingForm((current) => ({ ...current, staffDescription: event.target.value }))}
                          rows={2}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none"
                          placeholder="Guest-facing talking points"
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-slate-600 uppercase mb-1">Allergen info</label>
                          <textarea
                            value={briefingForm.allergenInfo}
                            onChange={(event) => setBriefingForm((current) => ({ ...current, allergenInfo: event.target.value }))}
                            rows={2}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none"
                            placeholder="Nuts, dairy, gluten..."
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 uppercase mb-1">Ingredients list</label>
                          <textarea
                            value={briefingForm.ingredientsList}
                            onChange={(event) => setBriefingForm((current) => ({ ...current, ingredientsList: event.target.value }))}
                            rows={2}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none"
                            placeholder="Key ingredients staff should know"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-slate-600 uppercase mb-1">Pairing suggestions</label>
                          <textarea
                            value={briefingForm.pairingSuggestions}
                            onChange={(event) => setBriefingForm((current) => ({ ...current, pairingSuggestions: event.target.value }))}
                            rows={2}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none"
                            placeholder="Comma or line separated"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 uppercase mb-1">Prep notes</label>
                          <textarea
                            value={briefingForm.prepNotes}
                            onChange={(event) => setBriefingForm((current) => ({ ...current, prepNotes: event.target.value }))}
                            rows={2}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none"
                            placeholder="Kitchen or plating notes"
                          />
                        </div>
                      </div>

                      <div className="flex justify-end">
                        <button
                          onClick={() => saveBriefingMutation.mutate()}
                          disabled={saveBriefingMutation.isPending}
                          className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-slate-900 rounded-lg hover:bg-slate-800 disabled:opacity-50"
                        >
                          {saveBriefingMutation.isPending ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Save className="w-4 h-4" />
                          )}
                          Save Menu Notes
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
